import Box from "ds/components/Box";
import Callout from "ds/components/Callout";
import Link from "ds/components/Link";
import Typography from "ds/components/Typography";
import { KnownBlockerKind, RetryBlockedReason } from "shared/Run/getRunActions";
import { Module, Stack } from "types/generated";
import { getCommitLabel } from "utils/commit";
import { providerHasVcsHost } from "utils/vcs";
import { AccessResult } from "utils/access";
import TriggerButton from "views/Stack/Runs/components/TriggerButton";

type RunRetryBlockedInfoProps = {
  reason: RetryBlockedReason;
  stack: Stack | Module;
  trigger: AccessResult;
};

const KNOWN_BLOCKER_COPY: Record<KnownBlockerKind, string> = {
  localSource: "This run can't be retried - it was based on local source code.",
  moduleVersion: "This run can't be retried - it belongs to a module version.",
  missingCommit: "This run can't be retried - the stack has no tracked commit yet.",
};

const RunRetryBlockedInfo = ({ reason, stack, trigger }: RunRetryBlockedInfoProps) => {
  if (reason.kind !== "stackMoved") {
    const message =
      reason.kind === "backendBlocker" ? reason.message : KNOWN_BLOCKER_COPY[reason.kind];

    return (
      <Callout variant="info">
        <Typography tag="p">{message}</Typography>
      </Callout>
    );
  }

  if (stack.__typename !== "Stack" || !stack.trackedCommit) {
    return null;
  }

  const { provider, trackedCommit } = stack;
  const commitLabel = getCommitLabel(provider, trackedCommit.hash);
  // A provider without a VCS host has no commit page to link to.
  const hasCommitPage = providerHasVcsHost(provider);

  return (
    <Callout variant="warning">
      <Box direction="column" gap="small" align="start">
        <Typography tag="p">
          This run can&apos;t be retried - the stack has moved on to commit{" "}
          {!hasCommitPage && commitLabel}
          {hasCommitPage && (
            <Link href={trackedCommit.url} rel="noopener noreferrer" target="_blank" inheritFont>
              {commitLabel}
            </Link>
          )}
          .
        </Typography>
        {!trigger.result && trigger.message && <Typography tag="p">{trigger.message}</Typography>}
        <Box gap="medium" align="center">
          {trigger.result && (
            <TriggerButton stack={stack} variant="contrast" size="small" text="Trigger new run" />
          )}
          <Link href={`/stack/${stack.id}`} size="small">
            View runs
          </Link>
        </Box>
      </Box>
    </Callout>
  );
};

export default RunRetryBlockedInfo;
